import { useMemo } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Activity, CalendarClock, Download, Flame, HeartPulse, Waves } from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCycleLogs } from '@/hooks/useCycleLogs';
import { useCycleSettings } from '@/hooks/useCycleSettings';
import { useToast } from '@/hooks/useToast';
import { addDaysISO, formatShort } from '@/lib/cycle';
import { computeStats, downloadDoctorReport } from '@/lib/report';

const TOOLTIP_STYLE = {
  backgroundColor: 'white',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  fontSize: '14px',
} as const;

const FLOW_BAR_COLORS: Record<string, string> = {
  Light: 'hsl(335 85% 82%)',
  Medium: 'hsl(335 78% 66%)',
  Heavy: 'hsl(335 70% 50%)',
};

export default function ReportPage() {
  useSeoMeta({
    title: 'Better Me — Doctor Progress Report',
    description:
      'Visual summaries of your symptom patterns over the past 30 days, ready to share with your doctor.',
  });

  const { logs, prediction } = useCycleLogs();
  const { settings } = useCycleSettings();
  const { toast } = useToast();
  const stats = useMemo(() => computeStats(logs, 30), [logs]);
  const gentle = !settings.trackFlareUps;

  const handleDownload = () => {
    downloadDoctorReport(logs, stats, prediction);
    toast({
      title: 'Report downloaded',
      description: 'Open the file to print or share it with your care provider.',
    });
  };

  const statCards = [
    ...(settings.trackPain
      ? [
          {
            label: 'Average pain',
            value: `${stats.avgPain.toFixed(1)}/10`,
            icon: Activity,
            classes: 'bg-lavender text-lavender-foreground',
          },
          {
            label: 'High-pain days (≥7)',
            value: String(stats.flareDays),
            icon: Flame,
            classes: 'bg-blush text-blush-foreground',
          },
        ]
      : []),
    {
      label: 'Cycle length',
      value: prediction ? `${prediction.cycleLength} days` : '—',
      icon: CalendarClock,
      classes: 'bg-aqua text-aqua-foreground',
    },
    ...(settings.trackVitals
      ? [
          {
            label: 'Avg resting HR',
            value: stats.avgHeartRate ? `${stats.avgHeartRate} bpm` : '—',
            icon: HeartPulse,
            classes: 'bg-blush text-blush-foreground',
          },
          {
            label: 'Avg HRV',
            value: stats.avgHrv ? `${stats.avgHrv} ms` : '—',
            icon: Waves,
            classes: 'bg-lavender text-lavender-foreground',
          },
        ]
      : []),
  ];

  const maxSymptomCount = stats.symptomFreq[0]?.count ?? 1;

  return (
    <div className="space-y-5">
      {/* Header + download */}
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Doctor progress report</h1>
        <p className="text-base text-muted-foreground">
          Symptom patterns over the past 30 days — bring this to your next appointment.
        </p>
      </div>

      <Button
        onClick={handleDownload}
        className="h-14 w-full rounded-2xl text-lg font-bold shadow-sm"
      >
        <Download className="size-5" aria-hidden />
        Download doctor summary
      </Button>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {statCards.map(({ label, value, icon: Icon, classes }) => (
          <Card key={label} className="rounded-3xl shadow-sm">
            <CardContent className="flex items-center gap-3 p-4">
              <span
                className={`flex size-11 shrink-0 items-center justify-center rounded-2xl ${classes}`}
              >
                <Icon className="size-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="truncate text-lg font-bold leading-tight">{value}</p>
                <p className="truncate text-xs text-muted-foreground">{label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pain trend */}
      {settings.trackPain && (
      <Card className="rounded-3xl shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-bold">Pain trend — last 30 days</CardTitle>
          <p className="text-sm text-muted-foreground">
            Days at or above 7 are high-pain flare days.
          </p>
        </CardHeader>
        <CardContent>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.daily} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                <defs>
                  <linearGradient id="painGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  interval={6}
                  tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                />
                <YAxis
                  domain={[0, 10]}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value) => [`${value}/10`, 'Pain']}
                />
                <Area
                  type="monotone"
                  dataKey="pain"
                  stroke="var(--chart-1)"
                  strokeWidth={2.5}
                  fill="url(#painGradient)"
                  connectNulls
                  dot={false}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Symptom frequency */}
      <Card className="rounded-3xl shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-bold">Most frequent symptoms</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {stats.symptomFreq.map(({ name, count }) => (
            <div key={name} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold">{name}</span>
                <span className="text-muted-foreground">
                  {count} day{count === 1 ? '' : 's'}
                </span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-lavender-strong to-blush-strong"
                  style={{ width: `${Math.max(6, (count / maxSymptomCount) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Flow distribution */}
      {settings.trackFlow && (
      <Card className="rounded-3xl shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-bold">Flow days this month</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-52 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.flowCounts} margin={{ top: 8, right: 8, bottom: 0, left: -24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 13, fill: 'var(--muted-foreground)' }}
                />
                <YAxis
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value) => [`${value}`, 'Days']} />
                <Bar dataKey="days" radius={[10, 10, 0, 0]} maxBarSize={56}>
                  {stats.flowCounts.map((entry) => (
                    <Cell key={entry.flow} fill={FLOW_BAR_COLORS[entry.label] ?? 'var(--chart-1)'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Prediction summary */}
      {prediction && (
        <Card className="rounded-3xl border-aqua-strong/30 bg-gradient-to-br from-aqua/60 to-white shadow-sm">
          <CardContent className="space-y-1.5 p-5">
            <h3 className="text-base font-bold text-aqua-foreground">Looking ahead</h3>
            <p className="text-[15px] leading-relaxed text-foreground/85">
              Next period predicted around <strong>{formatShort(prediction.nextPeriodStart)}</strong>{' '}
              (cycle length ~{prediction.cycleLength} days).{' '}
              {gentle ? (
                <>
                  Predicted bleeding window{' '}
                  <strong>
                    {formatShort(prediction.nextPeriodStart)} –{' '}
                    {formatShort(addDaysISO(prediction.nextPeriodStart, 4))}
                  </strong>
                </>
              ) : (
                <>
                  High-pain flare-up likely{' '}
                  <strong>
                    {formatShort(prediction.flareDates[0])} –{' '}
                    {formatShort(prediction.flareDates[prediction.flareDates.length - 1])}
                  </strong>
                </>
              )}
              . Last recorded period started {formatShort(prediction.lastPeriodStart)}.
            </p>
          </CardContent>
        </Card>
      )}

      <p className="text-center text-xs leading-relaxed text-muted-foreground">
        Better Me summarizes self-reported symptoms and wearable-synced metrics. It supports
        conversations with your care team — it is not a diagnosis.
      </p>
    </div>
  );
}
